import React, {useState, useEffect} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {RouteComponentProps} from 'react-router-dom';
import {withRouter} from 'react-router-dom';
import {AssetGroup, Bundle} from '@deviceinsight/ng-ui-api-client';
import {fetchAllBundles} from '@deviceinsight/ng-ui-api-client';
import {ContentHeader, messageBoxSuccess} from '@deviceinsight/ng-ui-components';
import {Accordion, AccordionItem, SpinnerContainer, Button} from '@deviceinsight/ng-ui-basic-components';
import JobVariantForm from './create/JobVariantForm';
import JobDetailsForm from './create/JobDetailsForm';
import {ADMIN_CREATE_TEST_IDS, ADMIN_CREATE_JOB_VARIANT_CONFIGS} from './consts';
import type {CreateJobFormData} from './types';
import {createGlobalJob} from '../../api/updateJobsApi';
import './AdminCreate';
import getAllVariantsDefined from './create/getAllVariantsDefined';
import {getBlacklistedTenantIds, getBundleAndVersion} from './utils';
import Blacklist from './Blacklist';
type Props = {
	assetGroup: AssetGroup;
} & RouteComponentProps;
const HEADER_TITLE = {
	id: 'globaljobs.list.create',
	defaultValue: 'Create global job'
};
const INITIAL_FORM_STATE = {
	...ADMIN_CREATE_JOB_VARIANT_CONFIGS,
	latestExecutionDate: undefined,
	description: undefined
} as unknown as CreateJobFormData;

const onSuccessfulCreate = (assetGroupId: number, push: (arg0: string) => void) => {
	messageBoxSuccess(<Trans i18nKey="updatejobs.create.createSuccess">The job was successfully created</Trans>);
	push(`/assetgroups/${assetGroupId}/globaljobs`);
};

const GlobalCreate = ({assetGroup, history: {push}}: Props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [bundles, setBundles] = useState<Bundle[]>([]);
	const [formValues, setFormValues] = useState<CreateJobFormData>(INITIAL_FORM_STATE);

	const submit = () => {
		setLoading(true);
		const data = {
			description: formValues.description,
			latestExecutionDate: formValues.latestExecutionDate,
			blacklistIds: getBlacklistedTenantIds(),
			boxBundleData: getBundleAndVersion(formValues.boxBundleData),
			lightBundleData: getBundleAndVersion(formValues.lightBundleData),
			ioBundleData: getBundleAndVersion(formValues.ioBundleData)
		};
		createGlobalJob(data)
			.then(() => {
				onSuccessfulCreate(assetGroup.id, push);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		setLoading(true);
		fetchAllBundles()
			.then(setBundles)
			.finally(() => setLoading(false));
	}, []);

	const allVariantsDefined = getAllVariantsDefined(formValues);

	return (
		<div className="admin-create">
			<ContentHeader title={HEADER_TITLE} parentPageLink={true} />
			<SpinnerContainer show={loading}>
				<div
					className="global-job-hint"
					style={{
						marginBottom: 20
					}}
					data-testid="global-job-hint">
					<Trans i18nKey="globaljobs.create.chooseBoxAndLight">
						Please enter a KIS.BOX, KIS.LIGHT, and KIS.IO version to create a Global Job.
					</Trans>
				</div>
				<div className="job-create-form column-wrapper" data-testid={ADMIN_CREATE_TEST_IDS.FORM}>
					<div className="job-create-variants column" data-testid={ADMIN_CREATE_TEST_IDS.VARIANTS}>
						{Object.keys(ADMIN_CREATE_JOB_VARIANT_CONFIGS).map((key) => {
							const config = ADMIN_CREATE_JOB_VARIANT_CONFIGS[key];
							return (
								<Accordion key={key}>
									{[
										<AccordionItem
											key={config.key}
											title={<Trans i18nKey={config.name.id}>{config.name.defaultValue}</Trans>}>
											<JobVariantForm
												bundles={bundles}
												formData={(formValues as Record<string, any>)[key]}
												setFormData={(data) => {
													setFormValues({...formValues, [key]: data});
												}}
											/>
										</AccordionItem>
									]}
								</Accordion>
							);
						})}
					</div>
					<div className="job-create-details column" data-testid={ADMIN_CREATE_TEST_IDS.DETAILS}>
						{allVariantsDefined && (
							<JobDetailsForm
								heading={{
									i18nKey: 'globaljobs.create.jobDetails',
									defaultValue: 'Global Job Details'
								}}
								formData={formValues}
								setFormData={setFormValues}
							/>
						)}
						<Blacklist
							show={allVariantsDefined}
							label={{
								i18nKey: 'globaljobs.create.blacklist',
								defaultValue: 'The following customers are excluded from the update'
							}}
						/>
						{allVariantsDefined && (
							<>
								<Button data-testid="create-job-button" primary onClick={submit}>
									<Trans i18nKey="globaljobs.create.scheduleButton">
										Schedule global firmware update
									</Trans>
								</Button>
							</>
						)}
					</div>
				</div>
			</SpinnerContainer>
		</div>
	);
};

export default withRouter(GlobalCreate);
